import { Injectable } from '@angular/core'
import { CreateEditTransactionService } from '@helios/create-transactions-modal'
import { BlankSessionService } from '@helios/sessions'
import { ModalPositionService } from '@helios/user-preferences'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { EditTransactionNoteModalComponent } from 'libs/generic-components/src/lib/edit-transaction-note-modal/edit-transaction-note-modal.component'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { TransactionDeleteNoteModalComponent } from 'libs/generic-components/src/lib/transaction-delete-note-modal/transaction-delete-note-modal.component'
import {
  BulkClearwaterTransaction,
  BulkCwTranNoteCommandType,
  Transactions,
} from '../models/clearwater-transaction.model'

@Injectable({
  providedIn: 'root',
})
export class BulkClearwaterTransactionsTaskService {
  constructor(
    private readonly modalService: NgbModal,
    private readonly createEditService: CreateEditTransactionService,
    private readonly blankSessionService: BlankSessionService,
    readonly modalPositionService: ModalPositionService,
  ) {}

  /**
   * Initiate the Edit Transaction Note dialog
   */
  async editNotes(
    transactions: BulkClearwaterTransaction[],
  ): Promise<void[] | void> {
    if (transactions.length === 0) {
      return
    }

    const compKey = 'edit-transaction-note'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(EditTransactionNoteModalComponent, {
      keyboard: false,
      windowClass: uniqueId,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    })
    const editModal =
      modalRef.componentInstance as EditTransactionNoteModalComponent
    const noteText =
      transactions.length === 1 ? transactions[0].tranNote ?? '' : ''
    editModal.noteText = noteText

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    let newNoteText!: string
    try {
      newNoteText = await modalRef.result
    } catch (e) {
      /*modal cancelled, nothing to do*/
      return
    }

    return this.saveNoteChanges(transactions, newNoteText)
  }

  /**
   * Delete note for selected transactions
   */
  async deleteNotes(
    cwTrans: BulkClearwaterTransaction[],
  ): Promise<void[] | void> {
    if (!cwTrans.length) return

    try {
      await this.openDeleteNoteModal()
    } catch (err) {
      /* modal cancelled, nothing to do */
      return
    }

    return this.deleteNoteChanges(cwTrans)
  }

  /**
   * Save a note for a multiple transactions
   */
  private async saveNoteChanges(
    cwTrans: BulkClearwaterTransaction[],
    newValue: string,
  ): Promise<void> {
    await this.blankSessionService.createBlankSessionIfNotInSession()

    const transaction = this.transactionWrapper(cwTrans)
    const editNoteCalls = this.createEditService.editNoteBulk(
      transaction,
      BulkCwTranNoteCommandType.EDIT_NOTE,
      newValue,
    )

    return editNoteCalls.toPromise()
  }

  /**
   * Delete notes for a multiple transactions
   */
  private async deleteNoteChanges(
    cwTrans: BulkClearwaterTransaction[],
  ): Promise<void> {
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const transaction = this.transactionWrapper(cwTrans)
    const deleteNoteRequests = this.createEditService.editNoteBulk(
      transaction,
      BulkCwTranNoteCommandType.DELETE_NOTE,
      '',
      true,
    )
    return deleteNoteRequests.toPromise()
  }

  private async openDeleteNoteModal(): Promise<void> {
    return this.modalService.open(TransactionDeleteNoteModalComponent, {
      keyboard: false,
    }).result
  }

  transactionWrapper(cwTrans: BulkClearwaterTransaction[]) {
    const transaction: Transactions[] = []
    cwTrans.forEach(tran => {
      const data = {
        transactionData: {
          transactionId: tran.transactionId,
          originalId: tran.originalId,
          userId: tran.userId,
          accountId: tran.accountId,
        },
        metaData: {},
      } as Transactions
      transaction.push(data)
    })
    return transaction
  }
}
